import React from 'react';
import { LogoMark } from './Logo';

/**
 * LoadingState — calm, on-brand loading. Soft rotating mark + line of copy.
 * Use full-page or inline (set `compact`).
 */
export const LoadingState = ({ label = 'A moment…', compact = false }) => (
  <div
    className={`flex flex-col items-center justify-center gap-4 text-ink-muted ${
      compact ? 'py-12' : 'min-h-[60vh] py-24'
    }`}
  >
    <div className="relative">
      <LogoMark size={32} className="text-clay-600 animate-pulse" />
      <span
        aria-hidden
        className="absolute -inset-3 rounded-full ring-1 ring-clay-200/60 animate-ping"
      />
    </div>
    <p className="text-sm">{label}</p>
  </div>
);

/**
 * EmptyState — editorial blank-slate.
 * Title in display serif, supporting body, optional CTA.
 */
export const EmptyState = ({
  eyebrow,
  title,
  body,
  action,
  className = '',
}) => (
  <div
    className={`mx-auto max-w-md text-center py-16 sm:py-20 px-4 ${className}`}
  >
    <LogoMark size={28} className="text-clay-600 mx-auto mb-6" />
    {eyebrow && <div className="eyebrow mb-3">{eyebrow}</div>}
    <h3 className="font-display text-display-md text-ink leading-snug">
      {title}
    </h3>
    {body && (
      <p className="mt-3 text-ink-soft leading-relaxed">{body}</p>
    )}
    {action && <div className="mt-7 flex justify-center">{action}</div>}
  </div>
);

/**
 * ErrorState — same shape as EmptyState but tonally distinct.
 */
export const ErrorState = ({ title = "Something didn't load right", body, action }) => (
  <EmptyState
    eyebrow="Hmm"
    title={title}
    body={
      body ||
      "Try refreshing the page. If it keeps happening, drop us a line — we'd like to fix it."
    }
    action={action}
  />
);

export default { LoadingState, EmptyState, ErrorState };
